/**
 * Shared API base used by both api/client.ts (document/queue/sign calls)
 * and auth/OAuthContext.tsx (the OAuth endpoints). In production this is
 * relative ("/api") — the frontend and API sit behind the same
 * orderflow.meatrix360.com CloudFront distribution (see modules/compute's
 * ordered_cache_behavior for /api/*), so a same-origin request needs no
 * absolute URL and the refresh-token cookie behaves as a normal
 * first-party cookie. For local `npm run dev` (Vite on localhost:5173,
 * which CloudFront doesn't front), set VITE_API_URL to the raw API
 * Gateway endpoint from `terraform output api_endpoint` instead.
 */
export const API_BASE = `${import.meta.env.VITE_API_URL ?? ""}/api`;

/**
 * Shared response handler: parses a fetch Response's JSON body, or throws
 * a plain Error carrying the backend's `{error: "..."}` message (or the
 * raw status text if the body wasn't JSON) on a non-2xx. Originally lived
 * only in auth/OAuthContext.tsx; moved here in Phase 5 so the new
 * unauthenticated api/signupClient.ts doesn't duplicate it — this
 * function is pure (touches no token/cookie/credential state), so
 * extracting it doesn't change any of OAuthContext.tsx's actual auth
 * logic, just where this one helper is defined.
 */
export async function parseJsonOrThrow(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => "");
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON error body — fall through, message below handles it
  }
  if (!res.ok) {
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : res.statusText;
    throw new Error(message);
  }
  return body;
}
